import { DeleteOutlined, EditFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Switch, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
    changeCityParameter,
    deleteCityRecord,
    fetchCityRecords,
    insertCityRecord,
    updateCityRecord,
} from '../../../redux/slice/city.slice';
import DebouncedSearch from '../../Common/DebouncedSearch';
import CommonTable from '../../Common/AdvancedTable';

const inputClass = `form-control
block
w-full
px-3
py-1.5
text-base
font-normal
text-gray-700
bg-white bg-clip-padding
border border-solid border-gray-300
rounded
transition
ease-in-out
m-0
focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none`;

export default function CityMaster() {
    const City = useSelector((state) => state.city);
    const dispatch = useDispatch();

    const [pagination, setPagination] = React.useState({
        pageNo: 0,
        limit: 15,
        count: 0,
        sortBy: 1,
        sortField: '_id',
        keywords: '',
    });

    useEffect(() => {
        setPagination({
            pageNo: City.pageNo || 0,
            limit: City.limit || 15,
            count: City.count || 0,
            sortBy: City.sortBy || 1,
            sortField: City.sortField || '_id',
            keywords: City.keywords || '',
        });
        fetchRecord({
            pageNo: City.pageNo || 0,
            limit: City.limit || 15,
            sortBy: City.sortBy || 1,
            sortField: City.sortField || '_id',
            keywords: City.keywords || '',
        });
    }, []);

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            count: City.count || 0,
        }));
    }, [City.count]);

    function fetchRecord({ pageNo, limit, sortBy, sortField, keywords }) {
        dispatch(fetchCityRecords({ pageNo, limit, sortBy, sortField, keywords }));
    }

    const handleDeleteSlide = (id) => {
        dispatch(deleteCityRecord({ id }));
        setTimeout(() => {
            fetchRecord(pagination);
        }, 100);
    };

    const handleSearch = (keywords) => {
        const updatedPagination = { ...pagination, pageNo: 0, keywords };
        setPagination(updatedPagination);
        dispatch(changeCityParameter(updatedPagination));
        dispatch(fetchCityRecords(updatedPagination));
    };

    const handleStateSubmit = async (data, id, setOpen, reset) => {
        try {
            if (id) {
                data._id = id;
                data.towns = data.towns.split(',');
                const re = await dispatch(updateCityRecord(data));
                if (!re?.error) {
                    setOpen(() => false);
                    reset();
                }
            } else {
                data.towns = data.towns.split(',');
                const re = await dispatch(insertCityRecord(data));
                if (!re?.error) {
                    setOpen(() => false);
                    reset();
                }
            }

            setTimeout(() => {
                dispatch(fetchCityRecords(pagination));
            }, 100);
        } catch (err) {
            alert(err.message);
        }
    };

    const onChangeTable = (paginate, filter, sorter, extra) => {
        const updatedSortField = sorter.field || '_id';
        const updatedSortBy = sorter.order === 'ascend' ? 1 : -1;

        const updatedPagination = {
            ...pagination,
            pageNo: paginate.current - 1,
            limit: paginate.pageSize,
            sortBy: updatedSortBy,
            sortField: updatedSortField,
            keywords: pagination.keywords,
        };

        setPagination(updatedPagination);
        dispatch(changeCityParameter(updatedPagination));
        dispatch(fetchCityRecords(updatedPagination));
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            sorter: (a, b) => a.name?.localeCompare(b.name),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            sorter: (a, b) => a.code?.localeCompare(b.code),
        },
        {
            title: 'State',
            dataIndex: 'state_name',
            key: 'state_name',
            width: 150,
            sorter: (a, b) => a.state_name?.localeCompare(b.state_name),
        },
        {
            title: 'Towns',
            dataIndex: 'towns',
            key: 'towns',
            width: 200,
            ellipsis: true,
            render: (e, record) => (Array.isArray(e) ? e.join(', ') : e),
            sorter: (a, b) => {
                const aTowns = Array.isArray(a.towns) ? a.towns.join(', ') : a.towns;
                const bTowns = Array.isArray(b.towns) ? b.towns.join(', ') : b.towns;
                return aTowns?.localeCompare(bTowns);
            },
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (e, record) => moment(e).format('lll'),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        },
        {
            title: 'Action',
            dataIndex: 'Action',
            key: 'Action',
            width: 150,
            fixed: 'right',
            render: (e, record) => (
                <div className="flex gap-2" key={record._id}>
                    <ManagerModel
                        key={record._id}
                        id={record._id}
                        submitHandler={handleStateSubmit}
                        _state={record}
                    />
                    <Button
                        title="Delete"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteSlide(record._id)}
                    ></Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl">Cities</h1>
                <div className="flex gap-2 ml-auto">
                    <DebouncedSearch
                        onSearch={handleSearch}
                        placeholder="Search Cities..."
                    />
                    <ManagerModel submitHandler={handleStateSubmit} />
                </div>
            </div>
            <CommonTable
                data={City.records}
                columns={columns}
                loading={City.loading}
                pagination={pagination}
                onChange={onChangeTable}
            />
        </div>
    );
}

function ManagerModel({ id, _state, submitHandler }) {
    const [open, setOpen] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
    } = useForm();

    const onSubmit = (body) => {
        submitHandler(body, id, setOpen, reset);
    };

    return (
        <div className="">
            <Button
                className={!id ? 'float-right mb-2' : ''}
                title={id ? 'Edit' : 'Add'}
                icon={id ? <EditFilled /> : <PlusOutlined />}
                onClick={() => setOpen(true)}
            >
                {id ? '' : 'Add'}
            </Button>

            <Modal
                centered
                title={(id ? 'Edit' : 'Add') + ' State'}
                open={open}
                onOk={() => setOpen(false)}
                footer={false}
                onCancel={() => setOpen(false)}
            >
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                State Name <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.state_name?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="state_name"
                                {...register('state_name', {
                                    required: 'field is required',
                                })}
                                aria-describedby="state_name"
                                placeholder="state name"
                                defaultValue={_state?.state_name || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Name <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.name?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="name"
                                {...register('name', { required: 'field is required' })}
                                aria-describedby="name"
                                placeholder="name"
                                defaultValue={_state?.name || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Code
                                {/* <span className="text-red-600">*</span> */}
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.code?.message}
                            </span>
                            <input
                                type="number"
                                className={inputClass}
                                name="code"
                                {...register('code')}
                                aria-describedby="code"
                                placeholder="code"
                                defaultValue={_state?.code || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Towns
                                {/* <span className="text-red-600">*</span> */}
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.towns?.message}
                            </span>
                            <textarea
                                rows={4}
                                type="text"
                                className={inputClass}
                                name="towns"
                                {...register('towns')}
                                aria-describedby="towns"
                                placeholder="towns"
                                defaultValue={_state?.towns || ''}
                            />
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded shadow hover:bg-gray-200"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
